import { useCallback, useEffect, useState } from 'react'

export interface MapSettings {
  showBorders: boolean
  hiddenSubIds: ReadonlySet<string>
  heatmapOpacity: number
}

const STORAGE_KEY = 'asf-heatmap-map-settings'

export const DEFAULT_HEATMAP_OPACITY = 0.7

const DEFAULT_SETTINGS: MapSettings = {
  showBorders: true,
  hiddenSubIds: new Set(),
  heatmapOpacity: DEFAULT_HEATMAP_OPACITY,
}

// The persisted shape. hiddenSubIds — not the visible set — is what's stored,
// so a sub area added after this blob was written defaults to visible rather
// than silently starting hidden.
interface StoredSettings {
  showBorders: boolean
  hiddenSubIds: string[]
  heatmapOpacity: number
}

function isStoredSettings(value: unknown): value is StoredSettings {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.showBorders === 'boolean' &&
    Array.isArray(candidate.hiddenSubIds) &&
    candidate.hiddenSubIds.every((id) => typeof id === 'string') &&
    typeof candidate.heatmapOpacity === 'number' &&
    Number.isFinite(candidate.heatmapOpacity) &&
    candidate.heatmapOpacity >= 0 &&
    candidate.heatmapOpacity <= 1
  )
}

// Never throws: a blob from an older version of this feature (or a user's
// tampered/corrupt storage) must fall back to the documented defaults rather
// than block the map from loading.
function loadSettings(): MapSettings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SETTINGS
    const parsed: unknown = JSON.parse(raw)
    if (!isStoredSettings(parsed)) return DEFAULT_SETTINGS
    return {
      showBorders: parsed.showBorders,
      hiddenSubIds: new Set(parsed.hiddenSubIds),
      heatmapOpacity: parsed.heatmapOpacity,
    }
  } catch {
    return DEFAULT_SETTINGS
  }
}

function persistSettings(settings: MapSettings) {
  const payload: StoredSettings = {
    showBorders: settings.showBorders,
    hiddenSubIds: Array.from(settings.hiddenSubIds),
    heatmapOpacity: settings.heatmapOpacity,
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // A private-browsing quota or disabled storage should cost the user
    // persistence, never the map itself.
  }
}

/**
 * The border toggle, per-sub-area visibility, and heatmap opacity — the
 * three settings the sheet edits — read from localStorage on mount and
 * re-persisted on every change, so a reload keeps the operator's choices.
 */
export function useMapSettings() {
  const [settings, setSettings] = useState<MapSettings>(() => loadSettings())

  useEffect(() => {
    persistSettings(settings)
  }, [settings])

  const toggleShowBorders = useCallback(() => {
    setSettings((prev) => ({ ...prev, showBorders: !prev.showBorders }))
  }, [])

  const toggleSub = useCallback((id: string) => {
    setSettings((prev) => {
      const hiddenSubIds = new Set(prev.hiddenSubIds)
      if (hiddenSubIds.has(id)) hiddenSubIds.delete(id)
      else hiddenSubIds.add(id)
      return { ...prev, hiddenSubIds }
    })
  }, [])

  const setHeatmapOpacity = useCallback((heatmapOpacity: number) => {
    setSettings((prev) => ({ ...prev, heatmapOpacity }))
  }, [])

  return {
    showBorders: settings.showBorders,
    hiddenSubIds: settings.hiddenSubIds,
    heatmapOpacity: settings.heatmapOpacity,
    toggleShowBorders,
    toggleSub,
    setHeatmapOpacity,
  }
}
